// src/store/selectors/sixDetail/index.ts
import { createSelector } from '@reduxjs/toolkit';

import type { RootState } from '@/store';
import type { FileEntry } from '@/types/SixDetail.types';
import { mapFileEntryToFileDetailPayload } from '@/store/mappers/mapSixDetail';
import { parseBreadcrumbFromPath } from '@/utils/pathBreadcrumb.utils';
import { getMembersCount } from '@/utils/sixDetail.utils';

export const selectSixDetailState = (state: RootState) => state.sixDetail;

export const selectSixId = createSelector(
  selectSixDetailState,
  (state) => state.sixId
);

function normalizePath(path: string | undefined): string | undefined {
  if (typeof path !== 'string') {
    return undefined;
  }

  const trimmedPath = path.replace(/\/+$/, '');

  if (trimmedPath.length === 0 || trimmedPath === '/') {
    return undefined;
  }

  return trimmedPath.startsWith('/') ? trimmedPath : `/${trimmedPath}`;
}

function getParentPath(path: string | undefined): string | undefined {
  if (typeof path !== 'string') {
    return undefined;
  }

  const normalizedPath = normalizePath(path);

  if (normalizedPath === undefined) {
    return undefined;
  }

  const segments = normalizedPath.split('/').filter(Boolean);

  if (segments.length <= 1) {
    return undefined;
  }

  return `/${segments.slice(0, -1).join('/')}`;
}

export const selectAllFiles = createSelector(
  selectSixDetailState,
  (state): FileEntry[] => state.files,
);

export const selectCurrentFolderPath = createSelector(
  selectSixDetailState,
  (state) => state.currentFolderPath,
);

export const selectFiles = createSelector(
  selectAllFiles,
  selectCurrentFolderPath,
  (files, currentFolderPath): FileEntry[] => {
    const normalizedCurrentPath = normalizePath(currentFolderPath);
    const visibleEntries = files.filter((entry) => getParentPath(entry.path) === normalizedCurrentPath);

    if (normalizedCurrentPath === undefined) {
      return visibleEntries;
    }

    const parentPath = getParentPath(normalizedCurrentPath);
    const parentEntry: FileEntry = {
      id: `nav:${normalizedCurrentPath}`,
      name: '../',
      kind: 'folder',
      isNavigationEntry: true,
      path: parentPath,
      modified: '',
      size: '',
    };

    return [parentEntry, ...visibleEntries];
  },
);

export const selectCurrentFolderFiles = selectFiles;

export const selectActivity = createSelector(
  selectSixDetailState,
  (state) => state.activity
);

export const selectParticipants = createSelector(
  selectSixDetailState,
  (state) => state.participants
);

export const selectSixDetailMeta = createSelector(
  selectSixDetailState,
  (state) => state.meta
);

export const selectSixDetailLoading = createSelector(
  selectSixDetailState,
  (detail) => detail.loadingFiles || detail.loadingActivity || detail.loadingParticipants,
);

export const selectSixDetailActiveFilters = createSelector(selectSixDetailState, (state) => state.activeFilters);

export const selectFolderBreadcrumb = createSelector(
  selectCurrentFolderPath,
  selectSixDetailMeta,
  (currentFolderPath, meta) => parseBreadcrumbFromPath(currentFolderPath, meta?.name ?? 'Root'),
);

export const selectSearchQuery = createSelector(selectSixDetailState, (state) => state.search);

export const selectSelectedFileIds = createSelector(selectSixDetailState, (state) => state.selectedFileIds);

export const selectFileSortKey = createSelector(selectSixDetailState, (state) => state.sortKey);

export const selectFileSortDir = createSelector(selectSixDetailState, (state) => state.sortDir);

export const selectFileDetailId = createSelector(selectSixDetailState, (state) => state.fileDetailId);

export function findMoveConflicts(
  allFiles: FileEntry[],
  itemIds: string[],
  targetFolderPath: string | undefined,
): string[] {
  const normalizedTarget = normalizePath(targetFolderPath);
  const movingItems = allFiles.filter((entry) => itemIds.includes(entry.id));
  const existingAtTarget = allFiles.filter(
    (entry) => getParentPath(entry.path) === normalizedTarget && !itemIds.includes(entry.id),
  );
  const existingNames = new Set(existingAtTarget.map((entry) => entry.name));
  return movingItems.filter((item) => existingNames.has(item.name)).map((item) => item.name);
}

export function isDescendantPath(
  parentPath: string | undefined,
  childPath: string | undefined,
): boolean {
  const normalizedParent = normalizePath(parentPath);
  const normalizedChild = normalizePath(childPath);

  if (normalizedParent === undefined || normalizedChild === undefined) return false;

  return normalizedChild.startsWith(`${normalizedParent}/`);
}

export const selectFileDetailPayload = createSelector(
  selectAllFiles,
  selectParticipants,
  selectFileDetailId,
  (files, participants, fileDetailId) => {
    if (fileDetailId === undefined) return undefined;

    const fileEntry = files.find((entry) => entry.id === fileDetailId);

    if (fileEntry === undefined) return undefined;

    return mapFileEntryToFileDetailPayload(fileEntry, getMembersCount(participants));
  },
);
